import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../axios/api";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import { FaArrowLeft, FaSave, FaTree } from "react-icons/fa";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import Button from "../components/common/styled components/Button";
import RichTextEditor from "../components/common/RichTextEditor";
import Loader from "../components/common/Loader";

const SpeciesForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isLoading, setIsLoading] = useState(false);
  const [initialData, setInitialData] = useState({
    label: "",
    common_name: "",
    characterstrics: "",
    references: "",
    form_factor: "",
    bd_biomass_density: "",
    sequence: "",
    species_image: null,
    existing_image_url: "",
  });

  const isEditMode = Boolean(id);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    label: Yup.string()
      .required("Species label is required *")
      .min(2, "Label must be at least 2 characters")
      .max(100, "Label must not exceed 100 characters"),

    common_name: Yup.string()
      .required("Common name is required *")
      .min(2, "Common name must be at least 2 characters")
      .max(100, "Common name must not exceed 100 characters"),

    characterstrics: Yup.string()
      .required("Characteristics are required *")
      .min(10, "Characteristics must be at least 10 characters"),

    references: Yup.string().nullable(),

    sequence: Yup.number()
      .required("Sequence is required *")
      .integer("Sequence must be a whole number")
      .min(1, "Sequence must be at least 1")
      .max(9999, "Sequence must not exceed 9999")
      .typeError("Sequence must be a valid number"),
  });

  // Fetch species if editing and data not passed via location state
  useEffect(() => {
    if (isEditMode) {
      if (location.state?.species) {
        const sp = location.state.species;
        setInitialData({
          id: sp.id,
          label: sp.label || "",
          common_name: sp.common_name || "",
          characterstrics: sp.characterstrics || "",
          references: sp.references || "",
          form_factor: sp.form_factor != null ? sp.form_factor : "",
          bd_biomass_density:
            sp.bd_biomass_density != null ? sp.bd_biomass_density : "",
          sequence: sp.sequence != null ? sp.sequence : "",
          species_image: null,
          existing_image_url: sp.icon_url || "",
        });
      } else {
        fetchSingleSpecies(id);
      }
    }
  }, [id, isEditMode, location.state]);

  const fetchSingleSpecies = async (speciesId) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/api/admin/getSpecies", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status && Array.isArray(response.data.data)) {
        const found = response.data.data.find(
          (item) => String(item.id) === String(speciesId)
        );
        if (found) {
          setInitialData({
            id: found.id,
            label: found.label || "",
            common_name: found.common_name || "",
            characterstrics: found.characterstrics || "",
            references: found.references || "",
            form_factor: found.form_factor != null ? found.form_factor : "",
            bd_biomass_density:
              found.bd_biomass_density != null ? found.bd_biomass_density : "",
            sequence: found.sequence != null ? found.sequence : "",
            species_image: null,
            existing_image_url: found.icon_url || "",
          });
        } else {
          toast.error("Species not found");
          navigate("/dashboard/myspecies");
        }
      }
    } catch (err) {
      console.error("Error fetching species:", err);
      toast.error("Failed to load species details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const token = localStorage.getItem("token");
    try {
      if (!isEditMode && !values.species_image) {
        toast.error("Species image is required for new species");
        setSubmitting(false);
        return;
      }

      const formData = new FormData();
      if (isEditMode) {
        formData.append("id", id);
      }
      formData.append("label", values.label.trim());
      formData.append("common_name", values.common_name.trim());
      formData.append("characterstrics", values.characterstrics.trim());
      formData.append("references", values.references ? values.references.trim() : "");
      formData.append(
        "form_factor",
        parseFloat(Number(values.form_factor || 0).toFixed(2))
      );
      formData.append(
        "bd_biomass_density",
        parseFloat(Number(values.bd_biomass_density || 0).toFixed(2))
      );
      formData.append(
        "sequence",
        parseFloat(parseFloat(values.sequence || 1).toFixed(1))
      );

      if (values.species_image) {
        formData.append("image", values.species_image);
        formData.append("image_changed", "true");
      } else if (isEditMode && values.existing_image_url) {
        formData.append("image_changed", "false");
        formData.append("existing_image_url", values.existing_image_url);
      }

      let response;
      if (isEditMode) {
        response = await api.put(`/api/admin/updateSpecies/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Species updated successfully!");
      } else {
        response = await api.post("/api/admin/createSpecies", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Species added successfully!");
      }

      if (response.data.status) {
        setTimeout(() => {
          navigate("/dashboard/myspecies");
        }, 800);
      } else {
        toast.error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error occurred while saving species");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ padding: isMobile ? "1rem" : "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Loader />
        </Box>
      ) : (
        <>
          {/* Header Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                type="button"
                variant="gray"
                onClick={() => navigate("/dashboard/myspecies")}
              >
                <FaArrowLeft size={13} style={{ marginRight: "6px" }} /> Back to Species
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 28,
                    bgcolor: "#10b981",
                    borderRadius: "4px",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', 'Outfit', 'Inter', sans-serif",
                    fontSize: { xs: "1.3rem", sm: "1.65rem" },
                    fontWeight: 700,
                    color: "#002F43",
                    letterSpacing: "-0.02em",
                    m: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {isEditMode ? `Edit Species: ${initialData.label || ""}` : "Add New Species"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Main Form Box */}
          <Formik
            initialValues={initialData}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({
              isSubmitting,
              errors,
              touched,
              values,
              setFieldValue,
              handleChange,
            }) => (
              <Form>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3.5 },
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Section 1: Basic Information */}
                    <Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2.5,
                        }}
                      >
                        <Field name="label">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Species Scientific Label *"
                              placeholder="e.g. Holoptelea integrifolia"
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              variant="outlined"
                              size="medium"
                              fullWidth
                            />
                          )}
                        </Field>

                        <Field name="common_name">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Common Name *"
                              placeholder="e.g. Kanju, Papdi, Indian Elm"
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              variant="outlined"
                              size="medium"
                              fullWidth
                            />
                          )}
                        </Field>
                      </Box>

                      {/* Image Upload Row */}
                      <FormControl fullWidth sx={{ mt: 2.5 }}>
                        <InputLabel shrink htmlFor="species_image" sx={{ fontWeight: 600 }}>
                          Species Icon / Image {!isEditMode && "*"}
                        </InputLabel>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                            mt: 1,
                            p: 2,
                            border: "1px dashed #cbd5e1",
                            borderRadius: 2,
                            background: "#f8fafc",
                          }}
                        >
                          <input
                            id="species_image"
                            type="file"
                            accept="image/*"
                            style={{
                              flex: "1 1 300px",
                              padding: "10px 14px",
                              border: "1px solid #cbd5e1",
                              borderRadius: "6px",
                              background: "#ffffff",
                              cursor: "pointer",
                            }}
                            onChange={(event) => {
                              const file = event.currentTarget.files[0];
                              setFieldValue("species_image", file);
                            }}
                          />

                          {values.species_image ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <img
                                src={URL.createObjectURL(values.species_image)}
                                alt="New Preview"
                                style={{
                                  width: 60,
                                  height: 60,
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  border: "2px solid #10b981",
                                }}
                              />
                              <Typography variant="caption" color="success.main" fontWeight={600}>
                                New Image Selected
                              </Typography>
                            </Box>
                          ) : values.existing_image_url ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <img
                                src={values.existing_image_url}
                                alt="Existing"
                                style={{
                                  width: 60,
                                  height: 60,
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  border: "1px solid #cbd5e1",
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Current Icon
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>

                        {!isEditMode && !values.species_image && touched.species_image && (
                          <FormHelperText error>Image is required for new species</FormHelperText>
                        )}
                      </FormControl>

                      {/* Numeric Metrics Row */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                          gap: 2.5,
                          mt: 2.5,
                        }}
                      >
                        <Field name="form_factor">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Form Factor"
                              placeholder="0.00"
                              type="number"
                              inputProps={{ step: "0.01", min: 0 }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              variant="outlined"
                              fullWidth
                            />
                          )}
                        </Field>

                        <Field name="bd_biomass_density">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Biomass Density"
                              placeholder="0.00"
                              type="number"
                              inputProps={{ step: "0.01", min: 0 }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              variant="outlined"
                              fullWidth
                            />
                          )}
                        </Field>

                        <Field name="sequence">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Sequence *"
                              placeholder="1"
                              type="number"
                              inputProps={{ min: 1, step: 1 }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              variant="outlined"
                              fullWidth
                            />
                          )}
                        </Field>
                      </Box>
                    </Box>

                    {/* Section 2: Characteristics Rich Editor */}
                    <Box sx={{ pt: 2, borderTop: "1px solid #e2e8f0" }}>
                      <Field name="characterstrics">
                        {({ field, form }) => (
                          <RichTextEditor
                            label="Species Characteristics *"
                            value={field.value || ""}
                            onChange={(content) =>
                              form.setFieldValue("characterstrics", content)
                            }
                            error={form.errors.characterstrics}
                            touched={form.touched.characterstrics}
                            placeholder="Enter detailed species characteristics. Use Bold, Italic, and Bullet lists, or click 'Insert Template'..."
                            showTemplateBtn={true}
                            templateType="characteristics"
                            minHeight="220px"
                          />
                        )}
                      </Field>
                    </Box>

                    {/* Section 3: References Rich Editor */}
                    <Box sx={{ pt: 2, borderTop: "1px solid #e2e8f0" }}>
                      <Field name="references">
                        {({ field, form }) => (
                          <RichTextEditor
                            label="References & Literature Sources"
                            value={field.value || ""}
                            onChange={(content) =>
                              form.setFieldValue("references", content)
                            }
                            error={form.errors.references}
                            touched={form.touched.references}
                            placeholder="Add literature citations, botanical research links, and reference URLs..."
                            showTemplateBtn={true}
                            templateType="references"
                            minHeight="180px"
                          />
                        )}
                      </Field>
                    </Box>

                    {/* Submit / Cancel Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        pt: 3,
                        borderTop: "1px solid #e2e8f0",
                      }}
                    >
                      <Button
                        type="button"
                        variant="cancel"
                        onClick={() => navigate("/dashboard/myspecies")}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                      >
                        <FaSave size={14} style={{ marginRight: "6px" }} />
                        {isSubmitting
                          ? isEditMode
                            ? "Updating..."
                            : "Saving..."
                          : isEditMode
                          ? "Update Species"
                          : "Save Species"}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Box>
  );
};

export default SpeciesForm;
